import { Queue, priorities } from '@nx-js/queue-util';
import { observable, observe, raw, unobserve, isObservable } from '@nx-js/observer-util';
export { observable as store } from '@nx-js/observer-util';
import React, { Component, PureComponent, Children } from 'react';
import PropTypes from 'prop-types';

const emptyTokens = new Set([undefined, '', '.']);
const notEmpty = token => !emptyTokens.has(token);

function normalizePath(fromPath, toPath, depth) {
  fromPath = toPathArray(fromPath);

  if (!toPath) {
    return fromPath;
  }

  const isAbsolute = toPath[0] === '/';
  toPath = toPathArray(toPath);

  // TODO: validate it!
  if (isAbsolute) {
    return toPath;
  }

  const normalizedPath = [];
  for (let token of toPath) {
    if (token === '..') {
      depth--;
    } else if (notEmpty(token)) {
      normalizedPath.push(token);
    }
  }
  return fromPath.slice(0, depth).concat(normalizedPath);
}

// convert pathname strings to arrays
function toPathArray(path = '') {
  return Array.isArray(path) ? path : path.split('/').filter(notEmpty);
}

// convert path arrays to absolute pathname strings
function toPathString(path = []) {
  // the leading '/' is important, it differentiates absolute pathnames from relative ones
  return typeof path === 'string' ? path : '/' + path.filter(notEmpty).join('/');
}

// convert params objects to query strings
function toQuery(params = {}) {
  const tokens = [];

  for (let key in params) {
    let value = params[key];
    if (notEmpty(value)) {
      key = encodeURIComponent(key);
      // JSON stringify the value to keep the type information
      value = encodeURIComponent(JSON.stringify(value));
      tokens.push(`${key}=${value}`);
    }
  }
  return tokens.length ? `?${tokens.join('&')}` : '';
}

// converts &key=value strings to objects
function toParams(queryString = '') {
  const queryTokens = queryString
  // remove the leading ? or # character
  .slice(1).split('&').filter(notEmpty);

  const params = {};
  for (let token of queryTokens) {
    const keyValue = token.split('=');
    const key = decodeURIComponent(keyValue[0]);
    // JSON.stringify and parse keeps the type information
    const value = JSON.parse(decodeURIComponent(keyValue[1]));
    params[key] = value;
  }
  return params;
}

// convert scroll anchor to URL hash
function toHash(scroll = {}) {
  return scroll.anchor ? `#${scroll.anchor}` : '';
}

// convert url hash to scroll anchor
function toScroll(hash) {
  return hash ? { anchor: hash.slice(1) } : undefined;
}

// convert object to url
function toUrl({ path, params, scroll }) {
  return toPathString(path) + toQuery(params) + toHash(scroll);
}

// augments the props with extraProps, which are not in excludeProps
// nice for proxying irrelevant props to the underlying DOM element
function addExtraProps(props, extraProps, excludeProps) {
  for (let key in extraProps) {
    if (!(key in excludeProps)) {
      props[key] = extraProps[key];
    }
  }
  return props;
}

// replace the data inside obj with the new data with mutations
// useful for 'replacing' observables while keeping the original reference
function replace(obj, data) {
  if (Array.isArray(obj)) {
    obj.splice(0, Infinity, ...data);
  } else {
    Object.keys(obj).forEach(key => delete obj[key]);
    Object.assign(obj, data);
  }
}

// run state change reactions synchronously
const sync = new Queue(priorities.SYNC);

// run integration reactions with a low priority
// URL and storage updates are not something the user is eagerly waiting for
const low = new Queue(priorities.LOW);

function start() {
  low.start();
  sync.start();
}

function process() {
  low.process();
  sync.process();
}

function stop() {
  low.stop();
  sync.stop();
}

const routers = [];
let routingStatus;
const initStatuses = new Set();

// register a router at a given depth
function registerRouter(router, depth) {
  // there can be multiple parallel routers at a given depth (we need a Set)
  if (!routers[depth]) {
    routers[depth] = new Set();
  }
  routers[depth].add(router);
  // route the newly added router, if we are not routing currently
  // or if the routing process is already past the current depth
  // this may happen if the router is not added because of the routing process
  // but was hidden some by conditional or lazy loading for example
  if (!routingStatus || depth <= routingStatus.depth) {
    initRouter(router);
  }
}

// router newly added routers even if there is no ongoing routing process
function initRouter(router) {
  const status = { cancelled: false };
  initStatuses.add(status);

  return Promise.resolve().then(() => router.startRouting()).then(
  // cancel the routing if a new routing started in the meantime
  resolvedData => !status.cancelled && router.finishRouting(resolvedData, status)).then(() => initStatuses.delete(status));
}

// release routers on componentWillUnmount
function releaseRouter(router, depth) {
  routers[depth].delete(router);
}

// this is part of the public API
// it cancels ongoing routings and recursively routes all routers from the root level
function route({ to, params: params$$1 = {}, session: session$$1 = {}, scroll, push } = {}, depth = 0) {
  // there may be routers which route outside of the standard routing process
  // for example a router was added by lazy loading and does its initial routing
  // cancel routings for these routers
  initStatuses.forEach(status => status.cancelled = true);
  // cancel the standard ongoing routing process if there is one
  if (routingStatus) {
    routingStatus.cancelled = true;
  } else {
    // flush the pending low priority URL changes to have a consistent state before starting a new routing
    // only flush it if there is no ongoing routing
    // otherwise the old and new routing should be treated in one batch to avoid flicker
    low.process();
    // the push option should default to true when the routing is not started
    // from an interception, otherwise it stays falsy
    if (push === undefined) {
      push = true;
    }
  }
  // stop all schedulers until the end of the routing and commit them at once
  // this includes state based view updates, URL updates and localStorag updates
  // having view updates during the routing can cause flickers
  // because of the random onRoute data resolve timings, it is nicer to commit everything at once
  stop();

  // create a new routing status
  // this may be cancelled by future routing processes
  // always start the routing from the root level router, even when it does not
  // produce any visual changes
  const status = routingStatus = { cancelled: false, depth: 0 };

  const toPath = to ? normalizePath(path, to, depth) : path;
  // push a new history item when necessary
  // it is important to do this before restarting the schedulers
  // to apply all new history replace operations to the new item
  // it is also important to do it before the view routing,
  // because the scroll position is saved here for the auto restoration
  if (push) {
    history.push({ path: toPath, params: params$$1, session: session$$1, scroll });
  } else {
    history.replace({ path: toPath, params: params$$1, session: session$$1, scroll });
  }

  // recursively route all routers, then finish the routing
  return Promise.resolve().then(() => switchRoutersFromDepth(status)).then(() => finishRouting({ scroll }, status));
}

// this recursively routes all parallel routers form a given depth
// all routers are finished routing at a depth before continuing with the next depth
function switchRoutersFromDepth(status) {
  const routersAtDepth = Array.from(routers[status.depth] || []);

  if (routersAtDepth.length) {
    return Promise.resolve()
    // during the first routing half, the user may intercept the routing
    // and cancel it by triggering a new routing
    // we have to wait for all routers before we can go on
    // to make sure the routing was not cancelled
    .then(() => startRoutingAtDepth(routersAtDepth, status))
    // after all routers finished the first half, all of them does the second half
    .then(resolvedData => finishRoutingAtDepth(routersAtDepth, resolvedData, status))
    // all routers finished routing at this depth, go on with the next depth
    .then(() => {
      ++status.depth;
      return switchRoutersFromDepth(status);
    });
  }
}

// do the first routing half if the routing is not cancelled
// it includes data resolving, interception and lazy loading
function startRoutingAtDepth(routersAtDepth, status) {
  if (!status.cancelled) {
    return Promise.all(routersAtDepth.map(router => router.startRouting()));
  }
}

// do the second routing half if the routing is not cancelled
function finishRoutingAtDepth(routersAtDepth, resolvedData, status) {
  if (!status.cancelled) {
    return Promise.all(routersAtDepth.map((router, i) => router.finishRouting(resolvedData[i], status)));
  }
}

// all routers updated recursively by now, it is time to finish the routing
// if it was not cancelled in the meantime
function finishRouting({ scroll }, status) {
  if (!status.cancelled) {
    // handle the scroll after the whole routing is over
    // this makes sure that the necessary elements are already rendered
    // in case of a scrollToAnchor behavior
    if (typeof scroll === 'object') {
      scroller.scrollTo(scroll);
      // scroll === false lets the browser do its default scroll restoration
    } else if (scroll !== false) {
      scroller.scrollTo({ top: 0, left: 0 });
    }

    // wait with anything resource intensive
    // until the whole routing is finished to keep it smooth
    // allowing view updates during the routing could also cause potential flicker
    // and tearing, depending on store implementations
    // the routing is over and the is no currently ongoing routing process
    process();
    start();

    routingStatus = undefined;
  }
}

const elements = {
  anchor: 'a',
  div: 'div'
};

const scroller = {
  scrollTo() {}
};

const storage = observable();
const params = observable();
const session = observable();
const path = observable([]);

const history = [{}];
let idx = 0;

function createHistoryItem(item = {}) {
  return {
    // raw (non Proxied) versions must be used here
    // Proxies can not be serialized by browsers
    path: Array.from(raw(item.path)),
    params: Object.assign({}, raw(item.params)),
    session: Object.assign({}, raw(item.session)),
    // scroll config can be passed without copying as it is read-only
    scroll: item.scroll,
    url: toUrl(item),
    idx
  };
}

// TODO: this must be batched! (for the scheduler)
function updateCurrent(item) {
  replace(path, item.path);
  replace(params, item.params);
  replace(session, item.session);
}

Object.defineProperties(history, {
  current: {
    get: () => history[idx]
  }
});

Object.assign(history, {
  push(item) {
    item = createHistoryItem(item);
    history.splice(++idx, Infinity, item);
    updateCurrent(item);
    return item;
  },
  replace(item) {
    item = createHistoryItem(item);
    history[idx] = item;
    updateCurrent(item);
    return item;
  },
  go(offset) {
    idx = Math.min(history.length - 1, Math.max(0, idx + offset));
    const { path, params, session } = history[idx];
    return route({
      to: toPathString(path),
      params,
      session,
      scroll: false,
      push: false
    });
  },
  get(offset) {
    const getIdx = Math.min(history.length - 1, Math.max(0, idx + offset));
    return history[getIdx];
  }
});

function syncHistory() {
  history.replace({ path, params, session, scroll: history.current.scroll });
}
// the URL and history can be updated with a low priority, the user won't notice
observe(syncHistory, { scheduler: low });

const originalPush = history.push;
const originalReplace = history.replace;
const originalGo = history.go;

Object.assign(history, {
  push(item) {
    item = originalPush.call(history, item);
    window.history.pushState(item, '', item.url);
  },
  replace(item) {
    item = originalReplace.call(history, item);
    window.history.replaceState(item, '', item.url);
  },
  go(offset) {
    originalGo.call(history, offset);
    return window.history.go(offset);
  }
});

window.addEventListener('popstate', ev => {
  const offset = history.current.idx < ev.state.idx ? 1 : -1;
  originalGo.call(history, offset);
});

Object.assign(scroller, {
  scrollTo(options) {
    if (options.anchor) {
      const anchor = document.getElementById(options.anchor);
      if (anchor) {
        anchor.scrollIntoView(options);
        window.scrollBy(options);
      }
    } else {
      window.scrollTo(options);
    }
  }
});

// do an initial scrolling if there is scroll data in the url hash
// and the page is not visited by a reload or history event in the same session
const RELOAD_KEY = 'STAX_RELOADED';
const RETRY_INTERVAL = 100;
const RETRY_TIMEOUT = 5000;
const scroll = toScroll(location.hash);

if (typeof scroll === 'object' && !sessionStorage.getItem(RELOAD_KEY)) {
  const start = Date.now();

  function initialScroll() {
    scroller.scrollTo(scroll);
    if (scroll.anchor) {
      // in case of a scroll anchor we have to wait for the anchor to
      // be mounted on the page, before the scrolling
      // do a simple retry tactic with a fixed interval and timeout in this case
      const hasAnchor = document.getElementById(scroll.anchor);
      const hasTime = Date.now() - start < RETRY_TIMEOUT;
      if (!hasAnchor && hasTime) {
        setTimeout(initialScroll, RETRY_INTERVAL);
      }
    }
  }
  initialScroll();
}
sessionStorage.setItem(RELOAD_KEY, true);

history.replace({
  path: toPathArray(location.pathname),
  params: toParams(location.search),
  scroll: toScroll(location.hash),
  session: history.current.session
});

const STORAGE_NAME = 'STAX_STORAGE';
Object.assign(storage, JSON.parse(localStorage.getItem(STORAGE_NAME)));

// auto sync the storage object with localStorage
function syncStorage() {
  localStorage.setItem(STORAGE_NAME, JSON.stringify(storage));
}
// localStorage sync can be done with a low priority, the user won't notice
observe(syncStorage, { scheduler: low });

// these are instrumentations

const tasks = new Set();
let batchCount = 0;

function add(task) {
  if (batchCount !== 0) {
    tasks.add(task);
  } else {
    runTask(task);
  }
}

function remove(task) {
  tasks.delete(task);
}

// must remove the task before executing it
// toherwise executed task caould start a new task list execution
// and recursively run itself infinite times
function runTask(task) {
  remove(task);
  task();
}

// this runs the passed function and delays all re-renders
// until the function is finished running
function batch(fn, ctx, args) {
  try {
    batchCount++;
    return fn.apply(ctx, args);
  } finally {
    batchCount--;
    if (batchCount === 0) {
      tasks.forEach(runTask);
    }
  }
}

// this creates and returns a batched version of the passed function
// the cache is necessary to always map the same thing to the same function
// which makes sure that addEventListener/removeEventListener pairs don't break
const cache = new Map();
function batchFn(fn) {
  if (typeof fn !== 'function') {
    return fn;
  }
  let batched = cache.get(fn);
  if (!batched) {
    batched = function (...args) {
      return batch(fn, this, args);
    };
    cache.set(fn, batched);
  }
  return batched;
}

// batched window.addEventListener(cb) like callbacks
function batchCallbacks(functionWithCallbacks) {
  return function batchedCallbacks(...args) {
    return functionWithCallbacks.apply(this, args.map(batchFn));
  };
}

// batches obj.onevent = fn like calls
function batchMethod(obj, method) {
  const descriptor = Object.getOwnPropertyDescriptor(obj, method);
  if (descriptor) {
    const newDescriptor = Object.assign({}, descriptor, {
      set(value) {
        return descriptor.set.call(this, batchFn(value));
      }
    });
    Object.defineProperty(obj, method, newDescriptor);
  }
}

// try to find the global object
// it is window in the DOM and global in NodeJS and React Native
let globalObj;
if (typeof window !== 'undefined') {
  globalObj = window;
} else if (typeof global !== 'undefined') {
  globalObj = global;
}

// do a sync batching for the most common task sources
// this should be removed when React's own batching is improved in the future
if (globalObj) {
  globalObj.setTimeout = batchCallbacks(globalObj.setTimeout);
  globalObj.setInterval = batchCallbacks(globalObj.setInterval);
  if (globalObj.requestAnimationFrame) {
    globalObj.requestAnimationFrame = batchCallbacks(globalObj.requestAnimationFrame);
  }
  if (globalObj.requestIdleCallback) {
    globalObj.requestIdleCallback = batchCallbacks(globalObj.requestIdleCallback);
  }
  // eslint-disable-next-line
  Promise.prototype.then = batchCallbacks(Promise.prototype.then);
  // eslint-disable-next-line
  Promise.prototype.catch = batchCallbacks(Promise.prototype.catch);

  // this batches websocket event handlers
  if (globalObj.WebSocket) {
    const websocketHandlers = ['onopen', 'onmessage', 'onerror', 'onclose'];
    websocketHandlers.forEach(method => batchMethod(globalObj.WebSocket.prototype, method));
  }
}

// batch addEventListener calls
if (globalObj.EventTarget) {
  EventTarget.prototype.addEventListener = batchCallbacks(EventTarget.prototype.addEventListener);
  EventTarget.prototype.removeEventListener = batchCallbacks(EventTarget.prototype.removeEventListener);
}

// HTTP event handlers are usually wrapped by Promises, which is covered above

const COMPONENT = Symbol('owner component');
const DUMMY_STATE = {};

function view(Comp, { devtool: rawDevtool } = {}) {
  const isStatelessComp = !(Comp.prototype && Comp.prototype.isReactComponent);
  const BaseComp = isStatelessComp ? Component : Comp;

  const devtool = rawDevtool ? operation => rawDevtool(Object.assign({ Component: Comp }, operation)) : undefined;

  // return a HOC which overwrites render, shouldComponentUpdate and componentWillUnmount
  // it decides when to run the new reactive methods and when to proxy to the original methods
  class ReactiveHOC extends BaseComp {
    constructor(props, context) {
      super(props, context);

      this.state = this.state || {};
      this.state[COMPONENT] = this;

      // run a dummy setState to schedule a new render, avoid forceUpdate
      const updater = () => this.setState(DUMMY_STATE);

      // create a reactive render for the component
      this.render = observe(this.render, {
        scheduler: {
          add: () => add(updater),
          delete: () => remove(updater)
        },
        debugger: devtool,
        lazy: true
      });
    }

    render() {
      return isStatelessComp ? Comp(this.props, this.context) : super.render();
    }

    // react should trigger updates on prop changes, while easyState handles store changes
    shouldComponentUpdate(nextProps, nextState) {
      const { props, state } = this;

      // respect the case when user prohibits updates
      if (super.shouldComponentUpdate && !super.shouldComponentUpdate(nextProps, nextState)) {
        devtool && devtool({ type: 'render', renderType: 'blocked' });
        return false;
      }

      // return true if it is a reactive render or state changes
      if (state !== nextState) {
        devtool && devtool({ type: 'render', renderType: 'reactive' });
        return true;
      }

      // the component should update if any of its props shallowly changed value
      const keys = Object.keys(props);
      const nextKeys = Object.keys(nextProps);
      if (nextKeys.length !== keys.length || nextKeys.some(key => props[key] !== nextProps[key])) {
        devtool && devtool({
          type: 'render',
          renderType: 'normal',
          props: nextProps,
          oldProps: props
        });
        return true;
      }
      return false;
    }

    // add a custom deriveStoresFromProps lifecyle method
    static getDerivedStateFromProps(props, state) {
      if (super.deriveStoresFromProps) {
        // inject all local stores and let the user mutate them directly
        const stores = mapStateToStores(state);
        super.deriveStoresFromProps(props, ...stores);
      }
      // respect user defined getDerivedStateFromProps
      if (super.getDerivedStateFromProps) {
        return super.getDerivedStateFromProps(props, state);
      }
      return null;
    }

    componentWillUnmount() {
      // call user defined componentWillUnmount
      if (super.componentWillUnmount) {
        super.componentWillUnmount();
      }
      // clean up memory used by Easy State
      unobserve(this.render);
    }
  }

  ReactiveHOC.displayName = Comp.displayName || Comp.name;
  // static props are inherited by class components,
  // but have to be copied for function components
  if (isStatelessComp) {
    for (let key of Object.keys(Comp)) {
      ReactiveHOC[key] = Comp[key];
    }
  }

  return ReactiveHOC;
}

function mapStateToStores(state) {
  // find store properties and map them to their none observable raw value
  // to do not trigger none static this.setState calls
  // from the static getDerivedStateFromProps lifecycle method
  const component = state[COMPONENT];
  return Object.keys(component).map(key => component[key]).filter(isObservable).map(raw);
}

// Router selects a single child to render based on its children's page props
// and the URL pathname token at the Router's depth (they can be nested)
class Router extends PureComponent {
  constructor(...args) {
    var _temp;

    return _temp = super(...args), this.state = {
      fromChild: null
    }, this.route = options => route(options, this.depth), this.saveContainer = container => this.container = container, _temp;
  }

  getChildContext() {
    return { staxDepth: this.depth + 1 };
  }

  // depth stores how nested is the router, root routers have a depth of 0
  get depth() {
    return this.context.staxDepth || 0;
  }

  componentDidMount() {
    registerRouter(this, this.depth);
  }

  componentWillUnmount() {
    releaseRouter(this, this.depth);
  }

  // this is part of the public API
  // it routes every router from the root depth


  // routing is split in 2 phases
  // first all parallel routers at the same depth executes startRouting
  // then all parallel routers at the same depth execute finishRouting
  startRouting() {
    const { onRoute, defaultPage, slave } = this.props;
    const fromPage = this.state.page;
    const toPage = path[this.depth] || defaultPage;
    // (parallel) slave routers do not update the URL pathname to avoid collisions
    if (!slave) {
      // fill the path with the default page, if the current path token is empty
      // this is important for relative links and automatic active link highlight
      path[this.depth] = toPage;
    }

    // onRoute is where do user can intercept the routing or resolve data
    if (onRoute) {
      return onRoute({
        target: this,
        fromPage,
        toPage
      });
    }
  }

  // finishRouting is called when all parallel routers at the current depth
  // finished executing startRouting
  // resolvedData is the data returned from props.onRoute in startRouting
  finishRouting(resolvedData, status) {
    const { defaultPage } = this.props;
    const fromPage = this.state.page;
    const toPage = path[this.depth] || defaultPage;

    // do not update the view if the page did not change
    // and there is no new resolved data from onRoute
    if (fromPage === toPage && !resolvedData) {
      return;
    }

    const nextState = {
      resolvedData,
      page: toPage
    };
    return new Promise(resolve => this.setState(nextState, resolve));
  }

  render() {
    const { element } = this.props;
    const { page, resolvedData } = this.state;
    let { fromChild } = this.state;

    let toChild = null;
    // if the resolvedData from onRoute is a React element use it as the view
    // this allows lazy loading components (and virtual routing)
    if (React.isValidElement(resolvedData)) {
      // validate the resolved child to have a page prop
      validateChild(resolvedData);
      toChild = resolvedData;
    } else {
      // select the next child based on the children's page prop
      // and the string token in the URL pathname at the routers depth
      toChild = this.selectChild(page);
      if (toChild && resolvedData) {
        // of there is resolvedData from onRoute, inject it as props to the next view
        toChild = React.cloneElement(toChild, resolvedData);
      }
    }

    return React.createElement(element,
    // forward none Router specific props to the underlying DOM element
    addExtraProps({}, this.props, Router.propTypes), toChild);
  }

  // select the next view based on the children's page prop
  // and the string token in the URL pathname at the routers depth
  selectChild(page) {
    const children = Children.toArray(this.props.children);
    // validate if all children has a page prop
    children.forEach(validateChild);
    const selectedChild = children.find(child => child.props.page === page);
    // if the router is mounted and it has no matching child view,
    // try to render a notFoundPage
    // if the router is not yet mounted, the initial routing is not yet finished
    // in this case it should render nothing (null), instead of a notFoundPage
    if (!selectedChild && this.container) {
      const { notFoundPage } = this.props;
      return children.find(child => child.props.page === notFoundPage);
    }
    return selectedChild;
  }
}

// all Router children must have a page prop
Router.propTypes = {
  defaultPage: PropTypes.string.isRequired,
  notFoundPage: PropTypes.string,
  onRoute: PropTypes.func,
  slave: PropTypes.bool,
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};
Router.defaultProps = {
  element: elements.div
};
Router.childContextTypes = { staxDepth: PropTypes.number };
Router.contextTypes = { staxDepth: PropTypes.number };
function validateChild(child) {
  if (typeof child.props.page !== 'string') {
    throw new Error('Every Router child must have a string valued, unique page prop');
  }
}

// Link is used to navigate between pages
// it can be relative ('home') or absolute ('/home'), just like vanilla HTML links
class Link extends PureComponent {
  constructor(...args) {
    var _temp;

    return _temp = super(...args), this.state = {}, this.onClick = ev => {
      const { to, params: params$$1, scroll, push, onClick } = this.props;

      // respect user defined onClick handlers on the Link
      // let the user prevent the routing by ev.preventDefault()
      if (onClick) {
        onClick(ev);
      }

      // if the event is not prevented, route all Routers
      // from the root level (use the absolute path for the link)
      if (!ev.defaultPrevented) {
        // prevent the default behavior of anchor clicks (page reload)
        ev.preventDefault();
        return route({
          to,
          params: params$$1,
          scroll,
          push
        }, this.depth);
      }
    }, _temp;
  }

  get depth() {
    return this.context.staxDepth || 0;
  }

  // gets the full path for relative and absolute links too
  get absolutePath() {
    return normalizePath(path, this.props.to, this.depth);
  }

  // automatically update the link activity on pathname and params changes
  // with a low priority scheduler, to do not have a performance impact during
  // page transitions
  componentDidMount() {
    this.activityUpdater = observe(() => this.setState({ isActive: this.isLinkActive() }), { scheduler: low });
  }

  // clean up transparent reactivity connections
  componentWillUnmount() {
    unobserve(this.activityUpdater);
  }

  isLinkActive() {
    const {
      activeClass,
      activeStyle,
      isActive,
      params: params$$1,
      scroll,
      push
    } = this.props;
    // only calculate link activity if there is an activeClass or activeSyle prop
    // otherwise it is not needed
    if (activeClass || activeStyle) {
      // let the user fine tune link activity with an isActive function prop
      if (isActive) {
        return isActive({
          path: this.absolutePath,
          params: params$$1,
          scroll,
          push
        });
      }
      return this.isLinkPathActive() && this.isLinkParamsActive() && this.isLinkScrollActive();
    }
  }

  isLinkPathActive() {
    // URL pathname tokens before this.depth always match with the link
    // otherwise the link and its containing Router would not be rendered
    // URL pathname tokens after the link does not affect the check
    // for example '/profile' link matches with '/profile/settings' URL
    return this.absolutePath.every((page, i) => page === path[i]);
  }

  isLinkParamsActive() {
    const linkParams = this.props.params;
    if (linkParams) {
      // extra query params does not affect the match
      // for example { a: 1 } link params matches with { a: 1, b: 2 } URL query
      const paramKeys = Object.keys(linkParams);
      return paramKeys.every(key => linkParams[key] === params[key]);
    }
    return true;
  }

  isLinkScrollActive() {
    const linkScroll = this.props.scroll;
    const historyScroll = history.current.scroll;
    // scroll positions match when the link or the URL is missing a scroll anchor
    // or when the two anchors macth
    if (linkScroll && historyScroll) {
      return linkScroll.anchor === historyScroll.anchor;
    }
    return true;
  }

  render() {
    let {
      to,
      params: params$$1,
      scroll,
      element,
      children,
      activeClass,
      activeStyle,
      style,
      className
    } = this.props;
    const { isActive } = this.state;
    const { onClick } = this;

    // calculate a full link href for correct 'Open link in new tab' behavior
    const href = toUrl({
      path: this.absolutePath,
      params: params$$1,
      scroll
    });

    if (isActive) {
      // both are empty strings by default
      className = `${className} ${activeClass}`;
      // both are empty objects by default
      style = Object.assign({}, style, activeStyle);
    }

    return React.createElement(element,
    // forward none Link specific props to the rendered element
    addExtraProps({ href, onClick, className, style }, this.props, Link.propTypes), children);
  }
}
Link.propTypes = {
  to: PropTypes.string,
  params: PropTypes.object,
  scroll: PropTypes.object,
  push: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object,
  activeClass: PropTypes.string,
  activeStyle: PropTypes.object,
  isActive: PropTypes.func,
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};
Link.defaultProps = {
  element: elements.anchor,
  className: '',
  activeClass: '',
  style: {},
  activeStyle: {}
};
Link.contextTypes = {
  staxDepth: PropTypes.number
};

export { view, batch, Router, Link, route, elements, scroller, storage, params, session, path, history };
